import { HelpCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLanguage } from "@/hooks/useLanguage";
import { trackEvent } from "@/lib/audit";

export type SportsTerm =
  | "h2h"
  | "highlights"
  | "stats"
  | "match_ai"
  | "global_oracle"
  | "halftime"
  | "rewards"
  | "heat_map"
  | "xg"
  | "pressure"
  | "fatigue"
  | "odds";

interface Props {
  term: SportsTerm;
  /** Optional override label; defaults to localized term label. */
  label?: string;
  /** Render label text alongside the help icon. Defaults to true. */
  showLabel?: boolean;
  className?: string;
  iconClassName?: string;
}

/**
 * Reusable, localized terminology label with help icon and tooltip.
 * Registers audit events when opened so we can measure clarity uplift.
 */
export function TermInfoLabel({
  term,
  label,
  showLabel = true,
  className,
  iconClassName,
}: Props) {
  const { t } = useLanguage();
  const labelText = label ?? t(`term_${term}_label`);
  const description = t(`term_${term}_desc`);
  const onOpen = (open: boolean) => {
    if (!open) return;
    void trackEvent("terminology_tooltip_opened", { term });
    void trackEvent("sports_term_explained", { term });
  };
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip onOpenChange={onOpen}>
        <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
          <span
            role="button"
            tabIndex={0}
            aria-label={`${labelText}: ${t("term_help_aria")}`}
            className={
              "inline-flex cursor-help items-center gap-1 rounded-full text-current outline-none focus-visible:ring-2 focus-visible:ring-primary/60 " +
              (className ?? "")
            }
          >
            {showLabel && <span className="font-bold">{labelText}</span>}
            <HelpCircle
              className={
                "h-3.5 w-3.5 text-muted-foreground/70 hover:text-primary " +
                (iconClassName ?? "")
              }
            />
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs space-y-1 text-[11px] leading-relaxed">
          <div className="font-bold tracking-wide">{labelText}</div>
          <div className="text-muted-foreground">{description}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}